import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { ShareCartPayload } from '@cart/const';
import { ActiveCartService, CartActions, CartApiService } from '@cart/data-access';
import { EnvironmentService } from '@core/feature';
import { Actions, EffectNotification, createEffect, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';
import { EMPTY, Observable, combineLatest, iif, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { CustomMultiCartService } from '../services';

@Injectable()
export class ShareCartEffects {
  shareCart$: Observable<CartActions.ShareCartSuccess | CartActions.ShareCartFail> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.SHARE_CART),
      switchMap(() =>
        combineLatest({
          cartId: this.activeCartService.getActiveCartId(),
        }).pipe(take(1)),
      ),
      switchMap(({ cartId }) => {
        return this.occCartService.shareCart(cartId).pipe(
          map((payload: ShareCartPayload) => new CartActions.ShareCartSuccess(payload)),
          catchError((error: unknown) => of(new CartActions.ShareCartFail(error))),
        );
      }),
    ),
  );

  handleShareLink$: Observable<never> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ROUTER_NAVIGATED),
        filter(
          ({
            payload: {
              routerState: { semanticRoute, queryParams },
            },
          }) => semanticRoute === 'cart' && !!queryParams['cloneFrom'],
        ),
        map(
          ({
            payload: {
              routerState: { queryParams },
            },
          }) => queryParams,
        ),
        tap((queryParams: Params) => {
          const cartId = queryParams['cloneFrom'];
          this.customMultiCartService.resetAnonymousCartState();
          this.customMultiCartService.cloneCart(cartId);
        }),
        switchMap(() => EMPTY),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private activeCartService: ActiveCartService,
    private occCartService: CartApiService,
    private customMultiCartService: CustomMultiCartService,
    private environmentService: EnvironmentService,
  ) {}

  ngrxOnRunEffects(resolvedEffects$: Observable<EffectNotification>): Observable<EffectNotification> {
    return iif(() => this.environmentService.isPlatformBrowser, resolvedEffects$, EMPTY);
  }
}
